import {
  defaultLoadableState,
  LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import React, { createContext, useContext, useMemo, useState } from 'react';
import type { Trade } from '~/contracts/common';

export type ContextType = {
  activeTrade: Trade | null;
  isTradeInApplied: boolean;
  setActiveTrade: (trade: Trade | null) => void;
  setStatusAgnosticTrade: (trade: Trade | null) => void;
  setTradeInApplied: (isTradeInApplied: boolean) => void;
  statusAgnosticTrade: Trade | null;
  trades: LoadableState<Trade[]>;
};

export const defaultContext: ContextType = {
  activeTrade: null,
  isTradeInApplied: true,
  setActiveTrade() {},
  setStatusAgnosticTrade() {},
  setTradeInApplied() {},
  statusAgnosticTrade: null,
  trades: defaultLoadableState<Trade[]>([])
};

const TradeContext = createContext(defaultContext);

export type OwnProps = React.PropsWithChildren<{
  isTradeInApplied?: boolean;
}>;

export function TradeContextProvider(props: OwnProps) {
  const [activeTrade, setActiveTrade] = useState(defaultContext.activeTrade);
  const [statusAgnosticTrade, setStatusAgnosticTrade] = useState(
    defaultContext.statusAgnosticTrade
  );
  const [isTradeInApplied, setTradeInApplied] = useState(
    props.isTradeInApplied ?? true
  );
  const trades = useLoadableState<Trade[]>([]);

  const value = useMemo<ContextType>(
    () => ({
      activeTrade,
      isTradeInApplied,
      trades,
      setActiveTrade,
      setStatusAgnosticTrade,
      setTradeInApplied,
      statusAgnosticTrade
    }),
    [activeTrade, statusAgnosticTrade, isTradeInApplied, trades]
  );

  return (
    <TradeContext.Provider value={value}>
      {props.children}
    </TradeContext.Provider>
  );
}

export const useGetTradeInStatus = (): {
  isActive: boolean;
  isExpired: boolean;
  isPending: boolean;
} => {
  const { status, vehicleValue } =
    useContext(TradeContext)?.statusAgnosticTrade ?? {};

  return {
    isActive: status === 'Active' && vehicleValue !== 0,
    isExpired: status === 'Expired',
    isPending: vehicleValue === 0
  };
};

export const useIsTradeLoaded = (): boolean => {
  const { trades } = useContext(TradeContext);

  return trades.isLoaded;
};

TradeContextProvider.displayName = 'TradeContextProvider';

export default TradeContext;
